import { ChevronLeft, ChevronRight, Lock } from 'lucide-react'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { Link } from 'react-router'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/elements/tooltip'
import StatusIcon from '@/components/StatusIcon/StatusIcon'
import { tagCanStart } from '@/utils/certificates'
import { cn } from '@/utils/helpers'

const STATUS_VARIANT = {
  'Not Started': 'secondary',
  'In Progress': 'warning',
  'Quiz Pending': 'warning',
  Completed: 'success',
}

/**
 * Sibling-module navigator for the in-cert course pages. Lets a student jump
 * to any other module under the same StudentCertificate — both directions,
 * regardless of canStart, so they can flip back to review an earlier module
 * even when later ones are in progress.
 *
 * Rendered inside the cert hero on CourseOverview and CourseComplete.
 */
function ModuleNavigator({ courseRecord, entityId, className = '' }) {
  // Tag siblings with canStart so the next-button can disable when the
  // student isn't allowed to skip ahead. Source-of-truth matches the
  // certificate-list view: you can only start the next module once every
  // prior module is Completed.
  const siblings = useMemo(
    () => tagCanStart(courseRecord?.studentCertificate?.courseRecords ?? []),
    [courseRecord],
  )
  const currentIndex = siblings.findIndex((r) => r.id === courseRecord?.id)
  if (siblings.length <= 1 || currentIndex === -1) return null

  const prev = siblings[currentIndex - 1] ?? null
  const next = siblings[currentIndex + 1] ?? null

  return (
    <nav aria-label="Module navigation" className={cn('grid grid-cols-2 gap-2', className)}>
      {/* On the first module there's no previous. Render a spacer rather than a
          "First module" placeholder — students found that confusing — so the
          Next button stays right-aligned. Prev is otherwise always navigable
          since every prior module is at least started. */}
      {prev ? (
        <ModuleLink record={prev} entityId={entityId} direction="prev" />
      ) : (
        <div aria-hidden="true" />
      )}
      <ModuleLink record={next} entityId={entityId} direction="next" />
    </nav>
  )
}

ModuleNavigator.propTypes = {
  courseRecord: PropTypes.object.isRequired,
  entityId: PropTypes.string.isRequired,
  className: PropTypes.string,
}

function ModuleLink({ record, entityId, direction }) {
  const isPrev = direction === 'prev'
  const Arrow = isPrev ? ChevronLeft : ChevronRight
  const label = isPrev ? 'Previous module' : 'Next module'

  // Only the forward slot renders a placeholder (on the last module). The
  // first-module back slot is rendered as a spacer by ModuleNavigator, so a
  // missing prev never reaches here.
  if (!record) {
    return (
      <span className="text-muted-foreground flex items-center justify-end gap-2 rounded-md border border-dashed px-3 py-2 text-xs">
        <span>Last module</span>
        <Arrow className="h-4 w-4 shrink-0" aria-hidden="true" />
      </span>
    )
  }

  const name = record.course?.name || 'Module'
  const status = record.status || 'Not Started'
  // canStart only matters for forward nav. Going back to a prior module
  // is always allowed since those are already started or completed.
  const isLocked = !isPrev && record.canStart === false

  const innerContent = (
    <>
      {isPrev && <Arrow className="h-4 w-4 shrink-0" aria-hidden="true" />}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-muted-foreground text-[0.625rem] tracking-wide uppercase">
          {label}
        </span>
        <span className="text-foreground truncate text-sm font-medium">{name}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {isLocked ? (
          <Badge variant="outline" size="default" className="gap-1">
            <Lock className="h-3 w-3" />
            Locked
          </Badge>
        ) : (
          <>
            <StatusIcon status={status === 'Quiz Pending' ? 'In Progress' : status} size="small" />
            <Badge variant={STATUS_VARIANT[status] || 'secondary'} size="default">
              {status}
            </Badge>
          </>
        )}
      </div>
      {!isPrev && <Arrow className="h-4 w-4 shrink-0" aria-hidden="true" />}
    </>
  )

  if (isLocked) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled
            aria-label={`${label} locked: ${name}`}
            className={cn(
              'border-input bg-background flex h-auto cursor-not-allowed items-center gap-2 rounded-md border-[1.5px] px-3 py-2 text-left opacity-60',
              isPrev ? 'justify-start' : 'justify-end',
            )}
          >
            {innerContent}
          </button>
        </TooltipTrigger>
        <TooltipContent>Complete the current module to unlock this one.</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Button
      asChild
      variant="outline"
      className={cn(
        'h-auto items-center gap-2 px-3 py-2 text-left',
        isPrev ? 'justify-start' : 'justify-end',
      )}
    >
      <Link
        to={`/accounts/${entityId}/students/courses/${record.id}`}
        aria-label={`${label}: ${name}`}
      >
        {innerContent}
      </Link>
    </Button>
  )
}

ModuleLink.propTypes = {
  record: PropTypes.object,
  entityId: PropTypes.string.isRequired,
  direction: PropTypes.oneOf(['prev', 'next']).isRequired,
}

export default ModuleNavigator
